import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { requestResetCode, verifyResetCode, resetPassword } from './api/passwordReset';

export const MIN_PASSWORD_LENGTH = 8;

export const inputClass =
  'w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition-colors focus:outline-none focus:border-secondary/50 focus:ring-4 focus:ring-secondary/10';
export const labelClass = 'block text-gray-600 text-sm font-semibold mb-1.5';

const STEP_COPY = {
  email: {
    title: 'Forgot password',
    subtitle: "Enter your account's email and we'll send you a verification code.",
  },
  code: {
    title: 'Check your email',
    subtitle: 'Enter the verification code we sent to',
  },
  password: {
    title: 'Set a new password',
    subtitle: `Choose a new password with at least ${MIN_PASSWORD_LENGTH} characters.`,
  },
  done: {
    title: 'Password updated',
    subtitle: 'Your password has been changed. You can now sign in with the new password.',
  },
};

export const Spinner = () => (
  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

export const SubmitButton = ({ isLoading, loadingLabel, children }) => (
  <button
    type="submit"
    disabled={isLoading}
    className="mt-1 w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:brightness-110 active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
  >
    {isLoading ? (
      <>
        <Spinner />
        <span>{loadingLabel}</span>
      </>
    ) : (
      children
    )}
  </button>
);

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const run = async (fn) => {
    setIsLoading(true);
    setError(null);
    setInfo(null);
    try {
      await fn();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email.');
      return;
    }
    run(async () => {
      await requestResetCode(email.trim());
      setStep('code');
    });
  };

  const handleResend = () =>
    run(async () => {
      await requestResetCode(email.trim());
      setInfo('A new code has been sent.');
    });

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('Please enter the verification code.');
      return;
    }
    run(async () => {
      const response = await verifyResetCode(email.trim(), code.trim());
      setResetToken(response?.data?.reset_token || '');
      setStep('password');
    });
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    run(async () => {
      await resetPassword(email.trim(), resetToken, password);
      setStep('done');
    });
  };

  const copy = STEP_COPY[step];

  return (
    <div className="min-h-dvh flex items-center justify-center bg-gradient-to-b from-gray-50/60 via-white to-white px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src="/pics/atk-long.svg" alt="ATK" className="w-52 sm:w-60" />
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-8">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">{copy.title}</h1>
            <p className="mt-1 text-sm text-gray-500 break-words">
              {copy.subtitle}
              {step === 'code' && <span className="font-semibold text-gray-700"> {email}</span>}
            </p>
          </div>

          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
              <div>
                <label className={labelClass} htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  autoFocus
                  className={inputClass}
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {error && <ErrorBox text={error} />}
              <SubmitButton isLoading={isLoading} loadingLabel="Sending...">Send code</SubmitButton>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleCodeSubmit} className="flex flex-col gap-4">
              <div>
                <label className={labelClass} htmlFor="code">Verification code</label>
                <input
                  type="text"
                  id="code"
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={`${inputClass} tracking-[0.3em] text-center font-semibold`}
                  placeholder="••••"
                  maxLength={4}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              {error && <ErrorBox text={error} />}
              {info && <p className="text-center text-sm text-emerald-600">{info}</p>}
              <SubmitButton isLoading={isLoading} loadingLabel="Verifying...">Verify code</SubmitButton>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    setError(null);
                    setInfo(null);
                  }}
                  className="text-gray-500 hover:text-gray-900"
                >
                  Change email
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isLoading}
                  className="font-semibold text-secondary hover:underline disabled:opacity-60"
                >
                  Resend code
                </button>
              </div>
            </form>
          )}

          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
              <div>
                <label className={labelClass} htmlFor="new-password">New password</label>
                <input
                  type="password"
                  id="new-password"
                  autoFocus
                  autoComplete="new-password"
                  className={inputClass}
                  placeholder="Enter a new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="confirm-password">Confirm password</label>
                <input
                  type="password"
                  id="confirm-password"
                  autoComplete="new-password"
                  className={inputClass}
                  placeholder="Repeat the new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              {error && <ErrorBox text={error} />}
              <SubmitButton isLoading={isLoading} loadingLabel="Saving...">Update password</SubmitButton>
            </form>
          )}

          {step === 'done' && (
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:brightness-110 active:scale-[0.99]"
            >
              Back to sign in
            </button>
          )}

          {step !== 'done' && (
            <div className="mt-6 text-center text-sm">
              <Link to="/login" className="text-gray-500 hover:text-gray-900">
                ← Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ErrorBox = ({ text }) => (
  <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-center text-sm font-medium text-red-600">
    {text}
  </p>
);

export default ForgotPassword;
